import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import Modal from 'components/atoms/Modal'
import SearchableSelect from 'components/SearchableSelect'
import ApproverPicker from 'components/ApproverPicker'
import { SkeletonLine } from 'components/Skeleton'
import organisationService from 'services/organisationService'
import { BORDER, INK, MUTED, ACCENT } from 'features/actions/workspace/ui'

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const describe = target => (target?.kind === 'sharepoint' ? `SharePoint: ${target.name}` : null)

const EditApproversModal = ({ target, organisationDomains = [], colleagues = [], onClose, onChanged }) => {
  const open = !!target
  const [userIds, setUserIds] = useState([])
  const [domains, setDomains] = useState([])
  const [check, setCheck] = useState(null)
  const [checking, setChecking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmFor, setConfirmFor] = useState(null)
  const [savedKey, setSavedKey] = useState('')
  const [savedAlready, setSavedAlready] = useState(false)

  const scopeKey = (ids, doms) => `${[...ids].sort().join(',')}|${[...doms].sort().join(',')}`

  useEffect(() => {
    if (!target) return
    const ids = (target.approvers || []).map(a => a.id)
    const doms = target.kind === 'domain' ? target.domains : []
    setUserIds(ids)
    setDomains(doms)
    setCheck(null)
    setError('')
    setConfirmFor(null)
    setSavedKey(scopeKey(ids, doms))
    setSavedAlready(false)
  }, [target])

  const people = useMemo(() => {
    const known = new Map(colleagues.map(c => [String(c.id), c]))
    ;(target?.approvers || []).forEach(a => { if (!known.has(String(a.id))) known.set(String(a.id), a) })
    return [...known.values()]
  }, [colleagues, target])

  const scopeBody = useMemo(
    () => (target?.kind === 'sharepoint' ? { customViewId: target.customViewId } : { domains }),
    [target, domains]
  )

  useEffect(() => {
    if (!open || userIds.length === 0 || (target.kind === 'domain' && domains.length === 0)) {
      setCheck(null)
      return undefined
    }
    let cancelled = false
    setChecking(true)
    organisationService.checkApprovers({ userIds, ...scopeBody })
      .then(res => { if (!cancelled) setCheck(res?.data || null) })
      .catch(() => { if (!cancelled) setCheck(null) })
      .finally(() => { if (!cancelled) setChecking(false) })
    return () => { cancelled = true }
  }, [open, userIds, scopeBody, target, domains.length])

  const nameOf = id => people.find(p => String(p.id) === String(id))?.name || 'This person'

  const persist = async () => {
    if (target.kind === 'sharepoint') {
      await organisationService.setSharePointApprovers({ buildingPassportId: target.buildingPassportId, customViewId: target.customViewId, userIds })
    } else {
      await organisationService.setDomainApprovers({ domains, userIds })
    }
    setSavedKey(scopeKey(userIds, domains))
  }

  const failure = err => err?.response?.data?.errors?.user_id?.[0]
    || err?.response?.data?.errors?.domains?.[0]
    || err?.response?.data?.message
    || 'We could not save this. Please try again.'

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await persist()
      toast.success(userIds.length === 0 ? 'Super Admins (default) will now approve these requests.' : 'Approvers saved.')
      onChanged?.()
      onClose()
    } catch (err) {
      setError(failure(err))
    } finally {
      setSaving(false)
    }
  }

  const giveAccess = async () => {
    const person = confirmFor
    setConfirmFor(null)
    setSaving(true)
    setError('')
    try {
      if (savedKey !== scopeKey(userIds, domains)) {
        await persist()
        setSavedAlready(true)
        toast.success('Approvers saved.')
        onChanged?.()
      }
      const res = await organisationService.giveApproverAccess({ userId: person.id, ...scopeBody })
      const granted = res?.data?.granted ?? person.cannotOpen
      toast.success(`${nameOf(person.id)} can now open ${plural(granted, 'document')}.`)
      const refreshed = await organisationService.checkApprovers({ userIds, ...scopeBody })
      setCheck(refreshed?.data || null)
    } catch (err) {
      setError(failure(err))
    } finally {
      setSaving(false)
    }
  }

  const needAccess = (check?.people || []).filter(p => p.eligible !== false && p.cannotOpen > 0)
  const notEligible = (check?.people || []).filter(p => p.eligible === false)
  const lockedAny = (check?.people || []).some(p => p.locked > 0)
  const heading = target?.kind === 'sharepoint' ? describe(target) : domains.map(d => `@${d}`).join(', ') || 'Choose a domain'

  return (
    <>
      <Modal
        open={open && !confirmFor}
        onClose={() => { if (!saving) onClose() }}
        title='Change approvers'
        withCancel
        cancelButtonText={savedAlready ? 'Close' : 'Cancel'}
        onCancel={onClose}
        withSubmit
        submitButtonText='Save approvers'
        disabled={saving || (target?.kind === 'domain' && domains.length === 0)}
        onSubmit={save}
      >
        <Body>
          <Lead>{heading}</Lead>
          <Help>
            An approver is a person who decides whether someone can open a locked document. Leave the list empty and your
            Super Admins decide, which is the default.
          </Help>

          {target?.kind === 'domain' && (
            <Field>
              <Label>Use the same approvers for these domains</Label>
              <SearchableSelect
                multiple
                ariaLabel='Domains that share these approvers'
                placeholder='Choose domains'
                value={domains}
                options={organisationDomains.map(d => ({ value: d, label: `@${d}` }))}
                onChange={setDomains}
              />
            </Field>
          )}

          <Field>
            <Label>Who approves requests for these documents?</Label>
            <ApproverPicker value={userIds} onChange={setUserIds} colleagues={people} />
          </Field>

          {checking && <SkeletonLine $h={18} $w='70%' />}

          {check && !checking && (
            <Notes>
              <Note>{check.notice}</Note>
              {lockedAny && <Note>{check.lockedNotice}</Note>}
              {notEligible.map(p => (
                <Warning key={p.id}>
                  {nameOf(p.id)} can&apos;t approve requests for your organisation, so the Super Admins will still decide them.
                </Warning>
              ))}
              {needAccess.map(p => (
                <Warning key={p.id}>
                  <span>{nameOf(p.id)} can&apos;t open {p.cannotOpen} of these documents yet.</span>
                  <ActionButton type='button' disabled={saving} onClick={() => setConfirmFor(p)}>Give them access now</ActionButton>
                </Warning>
              ))}
            </Notes>
          )}

          {error && <ErrorText>{error}</ErrorText>}
        </Body>
      </Modal>

      <Modal
        open={!!confirmFor}
        onClose={() => setConfirmFor(null)}
        title='Give access now'
        withCancel
        cancelButtonText='Cancel'
        onCancel={() => setConfirmFor(null)}
        withSubmit
        submitButtonText='Give access'
        onSubmit={giveAccess}
      >
        <Help>
          {confirmFor && `Give ${nameOf(confirmFor.id)} access to ${plural(confirmFor.cannotOpen, 'document')} now? `}
          They will be saved as an approver first, so any other changes you made here are saved too.
        </Help>
      </Modal>
    </>
  )
}

export default EditApproversModal

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: min(480px, 100%);
`

const Lead = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
`

const Help = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Notes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Note = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const Warning = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid #c2410c;
  border-radius: 8px;
  background: #ffedd5;
  font-size: 15px;
  color: #7c2d12;
`

const ActionButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  background: ${ACCENT};
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { cursor: not-allowed; opacity: 0.6; }
`

const ErrorText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #b00020;
  border-top: 1px solid ${BORDER};
  padding-top: 8px;
`
